import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col, ButtonGroup, Button, Dropdown } from 'react-bootstrap';

import { Helmet } from 'react-helmet';
import { capitalizeFirstLetter } from '../../../../util/text.js';

import { getProductPrice, getProductOption } from '../../ProductActions.js';
import { getUserGroup, USER_GROUP_DEFAULT } from '../../../User/UserGroupActions.js';
import { getLanguagePath, getTranslation } from '../../../Intl/IntlHelper.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import Icon from '../../../../components/Content/Icon.js';
import FormattedMessageConditionnal from '../../../Intl/components/Translate/FormattedMessageConditionnal.js';
import ProductListItem from './ProductListItem.js';
import ProductsFilters, { applyProductFilters, getActiveProductFilters } from './ProductsFilters.js';
import CmsPanel from '../../../Cms/components/content/CmsPanel.js';
import config from '../../../../config.js';

function ProductList({ intl, navigate, search, products = [], page = null, promotions = [], userGroup = null, categoryOrder = [], hideSorter = false, hideFilter = false, hideAddToCart = false, productsByPage = 10 }) {
    // const [sorterType, setSorterType] = useState('asc');
    // const [sorterKey, setSorterKey] = useState('category');
	const [itemsPerPage, setItemsPerPage] = useState(productsByPage || 0);

	const currentPageIndex = parseInt(search.get('page'), 10) || 1;
	const sorterType = search.get('sort') || 'asc';
	const sorterKey = search.get('sortBy') || 'category';

	// Keep the full list for the filter facets, then narrow it down with the filters selected in the url.
	const baseProducts = products;
	const activeFilters = getActiveProductFilters(search);
	products = applyProductFilters(products, activeFilters, { locale: intl.locale, userGroup, formatMessage: intl.formatMessage });
    // const handleSort = event => { setSorterKey(event.target.getAttribute('data-key') || event.target.parentNode.getAttribute('data-key')); setSorterType(event.target.getAttribute('data-type') || event.target.parentNode.getAttribute('data-type')); };
	products = []
		.concat(
			(categoryOrder || []).length && sorterKey === 'category'
			? (categoryOrder || []).map(productId => products.find(product => product._id === productId)).filter(product => product)
			: [],
		)
		.concat(products
			.filter(product => (
					sorterKey !== 'category'
					|| !(categoryOrder || []).length
					|| !categoryOrder.includes(product._id)
				)
				&& (
					!(getProductOption(product, 'visibleGroups') || []).length
					|| getProductOption(product, 'visibleGroups').includes((userGroup || {}).identifier)
			))
			.sort((productA, productB) => {
				switch(sorterKey) {
					case 'weight':
						return (sorterType === 'asc' ? productA : productB).weight - (sorterType === 'asc' ? productB : productA).weight;

					case 'price':
						return getProductPrice(sorterType === 'asc' ? productA : productB, 1, userGroup) - getProductPrice(sorterType === 'asc' ? productB : productA, 1, userGroup);

					case 'name':
					default:
						return getTranslation(sorterType === 'asc' ? productA : productB, 'name', intl.locale).localeCompare(getTranslation(sorterType === 'asc' ? productB : productA, 'name', intl.locale));
				}
			}));

	useEffect(() => {
		if(itemsPerPage && currentPageIndex > Math.ceil(products.length / itemsPerPage)) {
			navigate('?page=1');
		} else if(currentPageIndex > 1) {
			// window.document.getElementById('products-list')?.scrollIntoView({ behavior: 'instant', block: 'start' });
		}
	}, [currentPageIndex, sorterType, sorterKey]);

    const renderProducts = () => {
        let promotionCount = 0;

        const renderPromotion = () => {
            if((userGroup || {}).identifier === USER_GROUP_DEFAULT || (false && process.env.NODE_ENV === 'development')) {
                const promotion = promotions[promotionCount].text;
                promotionCount += 1;
                return (
                    <Col xs="12" className="mb-3">
                        <div className="px-3 py-4 bg-warning rounded">
                            <CmsPanel slug={promotion} />
                        </div>
                    </Col>
                );
            }
            return null;
        };

		const productsCountInPage = itemsPerPage || products.length;
		return (itemsPerPage ? products.slice((currentPageIndex - 1) * itemsPerPage, currentPageIndex * itemsPerPage) : products)
			.map((product, index, array) => (
				<Fragment key={product._id}>
					<Col xs="6" md="4" lg={productsCountInPage < 4 ? 6 : 3}>
						<ProductListItem product={product} listId="products-list" hideAddToCart={hideAddToCart} />
					</Col>
					{promotions[promotionCount] && index > 0 && index % Math.min(products.length - 1, products.length > 7 ? 8 : 4) === 0 && renderPromotion()}
				</Fragment>
			));
    };

    const renderListPagerAndSorter = (isAlt = false) => {
        if(!products.length) {
            return null;
        }
		// Preserve sort + active filters across the reloading pager links (everything but the page cursor).
		const extraParams = Array.from(search.entries()).filter(([key]) => key !== 'page').map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
		const queryParameters = extraParams ? `&${extraParams}` : '';
        return (
			<div className={classnames('d-md-flex', 'justify-content-end', 'border-light', isAlt ? ['mt-2'] : ['mb-2'])}>
				{(baseProducts.length > 0 || !hideSorter) && (
					<div className="d-flex justify-content-between align-items-center gap-2 mb-2">
						{!hideFilter && baseProducts.length > 0 && (
							<ProductsFilters products={baseProducts} />
						)}
						{!hideSorter && (
							<div className={classnames('text-center', 'text-lg-end')}>
								<Dropdown>
									<Dropdown.Toggle variant="outline-light" size="sm" className="category-sorter text-dark">
										<FormattedMessageConditionnal id={`categorySorter${capitalizeFirstLetter(sorterKey)}Action`} defaultMessage={`Sort by ${capitalizeFirstLetter(sorterKey)}`} />
									</Dropdown.Toggle>
									<Dropdown.Menu>
										{['name', 'weight', 'price'].map(availableSorterKey => {
											return ['asc', 'desc'].map(availableSorterType => {
												if(availableSorterKey === sorterKey && availableSorterType === sorterType) {
													return null;
												}
												return (
													<Dropdown.Item key={`${availableSorterKey}-${availableSorterType}`} data-key={availableSorterKey} data-type={availableSorterType} as="a" href={`?sortBy=${availableSorterKey}&sort=${availableSorterType}`}>
														<FormattedMessageConditionnal id={`categorySorter${capitalizeFirstLetter(availableSorterKey)}Action`} defaultMessage={`Sort by ${capitalizeFirstLetter(availableSorterKey)} ${availableSorterType}`} />
														{' '}<Icon icon={availableSorterType === 'asc' ? 'arrow-long-down' : 'arrow-long-up'} />
													</Dropdown.Item>
												);
											});
										})}
									</Dropdown.Menu>
								</Dropdown>
								{/* <Dropdown>
									<Dropdown.Toggle variant="link" size="sm" as="span" className="p-0" suppressHydrationWarning>
										{getCurrencySymbol(currencyData.currency)}
									</Dropdown.Toggle>
									<Dropdown.Menu className="position-absolute text-center" style={{ left: '-200%' }} suppressHydrationWarning>{currenciesNodes}</Dropdown.Menu>
								</Dropdown> */}
							</div>
					)}
					</div>
				)}
				{itemsPerPage > 0 && products.length > itemsPerPage && (
					<div>
						<ButtonGroup size="lg" className="w-100 my-5 my-md-0 border-light border-1 category-pager">
							<Button variant="outline-danger" as="a" href={currentPageIndex > 1 ? `?page=${currentPageIndex - 1}${queryParameters}` : ''} disabled={currentPageIndex <= 1} rel="prev" className="border-0 px-2">{'< '}</Button>
							{products.length > 0 && new Array(Math.ceil(products.length / itemsPerPage)).fill(1).map((v, index) => <Button key={index} variant={index + 1 === currentPageIndex ? 'danger' : 'outline-danger'} as="a" href={`?page=${index + 1}${queryParameters}`} className={classnames('mx-1', 'border', 'rounded', 'rounded-bottom-4', currentPageIndex === index + 1 && ['bg-danger'])}>{index + 1}</Button>)}
							<Button variant="outline-danger" as="a" href={products.length - (currentPageIndex * itemsPerPage) > 0 ? `?page=${currentPageIndex + 1}${queryParameters}` : ''} disabled={products.length - (currentPageIndex * itemsPerPage) <= 0} rel="next" className="border-0 px-2">{' >'}</Button>
						</ButtonGroup>
					</div>
				)}
			</div>
        );
    };

    return (
        <div id="products-list">

			{page && (
				<Helmet
					link={[
						currentPageIndex > 1 && {
							href: `${config.url}${getLanguagePath(`/${(page.slug || '').replace('*', '')}`, page.language).substring(1)}?page=${currentPageIndex - 1}`,
							rel: 'prev',
						},
						itemsPerPage && products.length > (currentPageIndex * itemsPerPage) && {
							href: `${config.url}${getLanguagePath(`/${(page.slug || '').replace('*', '')}`, page.language).substring(1)}?page=${currentPageIndex + 1}`,
							rel: 'next',
						},
					].filter(rel => rel)}
				/>
			)}

			{renderListPagerAndSorter()}

            <Row className="mb-3 g-3">
                {renderProducts()}
            </Row>

			{Object.keys(activeFilters).length > 0 && products.length === 0 && (
				<p className="text-center text-muted py-5">
					<FormattedMessage id="productFilterNoResults" defaultMessage="No product matches the selected filters." />
				</p>
			)}

			{products.length > 6 && renderListPagerAndSorter(true)}
        </div>
    );
}

function mapStateToProps(store, props) {
    return {
        userGroup: getUserGroup(store),
    };
}

ProductList.propTypes = {
    intl: PropTypes.object.isRequired,
	navigate: PropTypes.func.isRequired,
	search: PropTypes.object.isRequired,
    products: PropTypes.arrayOf(PropTypes.object),
	page: PropTypes.object.isRequired,
    userGroup: PropTypes.object,
    promotions: PropTypes.arrayOf(PropTypes.object),
    categoryOrder: PropTypes.arrayOf(PropTypes.string),
    hideSorter: PropTypes.bool,
    hideFilter: PropTypes.bool,
    hideAddToCart: PropTypes.bool,
    productsByPage: PropTypes.number,
};

export default connect(mapStateToProps)(withRouter(injectIntl(ProductList), ['navigate', 'search']));
